import { get } from "lodash";
import { judgeQuestionId, removeBr, removeSpace } from "./utils";
import { headerBlank, pageBreaks } from "./move";
import { Question, StringObj } from "../../type";
import { getSeqGroups } from "./utils";
import { bufferRows } from "../common";
import { questionTypes } from "../../config";
import { checkExcelFormatByQuestion } from "../review/excel";

const formColumns: StringObj = {
  "1": "questionId",
  "2": "questionType",
  "3": "rowType",
  "4": "itemId",
  "5": "text",
  "6": "exclusive",
  "7": "condition",
  "8": "creative",
  "9": "random",
  "10": "answerType",
  "11": "remarks",
  "12": "questionTitle",
};

// 空白を許可しないカラム
const noSpaceColumns = ["questionId", "questionType", "rowType", "exclusive", "answerType"];

const noBrColumns = [
  "questionId",
  "questionType",
  "rowType",
  "text",
  "exclusive",
  "condition",
  "creative",
  "random",
  "answerType",
  "remarks",
  "questionTitle",
];

function formatValue(value: string, key: string) {
  let cleanedValue = value.toString();
  cleanedValue = cleanedValue.replace("　", " ");
  if (cleanedValue) {
    if (noSpaceColumns.includes(key)) {
      cleanedValue = removeSpace(cleanedValue);
    }
    if (noBrColumns.includes(key)) {
      cleanedValue = removeBr(cleanedValue);
    }
    if (key == "condition" && cleanedValue.includes("＝")) {
      cleanedValue = cleanedValue.replace("＝", "=");
    }
    if (key == "questionType") {
      cleanedValue = cleanedValue
        .replace("ﾏﾄﾘｸｽ", "マトリクス")
        .replace("(", "（")
        .replace(")", "）");
    }
  }
  return cleanedValue;
}

export function transformQuestionsArrayToJSON(excelValues: string[][]) {
  let checkResults = [];

  const excelValuesJson = excelValues
    .filter((_, index) => index >= headerBlank)
    .map((row) => {
      const rowObj: StringObj = {};
      row.forEach((cell, i) => {
        if (+i >= 1 && +i <= 12) {
          const key = get(formColumns, i.toString(), i.toString());
          rowObj[key] = cell;
        }
      });
      return rowObj;
    });

  // データクレンジング
  excelValuesJson.forEach((row, index) => {
    let cleanedRow: StringObj = {};

    if (!row["questionId"]) {
      if (index == 0) {
        cleanedRow["questionId"] = excelValuesJson[index + 1]["questionId"].toString();
      } else {
        cleanedRow["questionId"] = excelValuesJson[index - 1]["questionId"].toString();
      }
    } else {
      cleanedRow["questionId"] = row["questionId"].toString();
    }

    Object.keys(row)
      .filter((i) => i != "questionId")
      .forEach((key) => {
        cleanedRow[key] = formatValue(row[key], key);
      });

    if (index > 0) {
      if (
        !row["rowType"] &&
        excelValuesJson[index - 1]["rowType"].includes("表側") &&
        //  || excelValuesJson[index - 1]["rowType"].includes("表頭")
        row["itemId"] &&
        row["itemId"].toString() !== "1" &&
        row["itemId"].toString() !== "小見出し"
      ) {
        cleanedRow["rowType"] = excelValuesJson[index - 1]["rowType"];
      }
    }

    excelValuesJson[index] = cleanedRow;
  });

  // 設問IDリストを作成
  const questionIds = excelValuesJson.reduce((acc, row) => {
    if (!["", "改", "CELL"].includes(row["questionId"]) && !acc.includes(row["questionId"])) {
      acc.push(row["questionId"]);
    }
    return acc;
  }, []);

  const questions = [];

  // 設問ごとに処理
  questionIds.forEach((qId) => {
    const targetIndexes = excelValuesJson.reduce((acc: number[], row, index) => {
      if (index == 0) {
        return acc;
      }

      if (row["questionId"] === qId) {
        acc.push(index);
      }
      return acc;
    }, []);

    // 設問番号が重複しているときにグループに分ける
    const questionIndexes = getSeqGroups(targetIndexes);

    if (questionIndexes.length === 1) {
      const q = questionIndexes[0];

      const targetRows = excelValuesJson.filter((_, index) => q.includes(index));
      const targetRowForward = excelValuesJson.slice(Math.max(q[0] - bufferRows, 0), q[0]);
      // const targetRowBackward = excelValuesJson
      //   .slice(q[q.length - 1] + 1, q[q.length - 1] + 1 + bufferRows)
      //   .filter((i) => i);

      const questionObj = getQuestionObj(qId, targetRows, targetRowForward);
      questions.push(questionObj);
      checkResults = checkResults.concat(checkExcelFormatByQuestion(qId, targetRows));
    } else if (questionIndexes.length > 1) {
      questionIndexes.forEach((q) => {
        const targetRows = excelValuesJson.filter((_, index) => q.includes(index));
        const targetRowForward = excelValuesJson.slice(Math.max(q[0] - bufferRows, 0), q[0]);
        // const targetRowBackward = excelValuesJson
        //   .slice(q[q.length - 1] + 1, q[q.length - 1] + 1 + bufferRows)
        //   .filter((i) => i);
        const questionObj = getQuestionObj(qId, targetRows, targetRowForward);
        questions.push(questionObj);
        checkResults = checkResults.concat(checkExcelFormatByQuestion(qId, targetRows));
      });
    }
  });

  return {
    questions,
    checkResults2: checkResults,
  };
}

function translationCellValueToJson(cellValues: any[][]) {
  return cellValues.map((row) => {
    const rowObj: StringObj = {};
    row.forEach((cell, i) => {
      if (+i >= 1 && +i <= 12) {
        const key = get(formColumns, i.toString(), i.toString());
        rowObj[key] = formatValue(cell.toString(), key);
      }
    });
    return rowObj;
  });
}

export async function getJson(
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  rangeText: string
) {
  const range = sheet.getRange(rangeText);
  range.load("values");
  await context.sync();
  const questionJson = translationCellValueToJson(range.values);
  return questionJson;
}

export function getQuestionObj(
  questionId: string,
  targetRows: StringObj[],
  targetRowsForward: StringObj[]
  // targetRowsBackward: StringObj[]
) {
  try {
    const loopCols = [
      "itemId",
      "text",
      // "exclusive",
      "condition",
      "random",
      "creative",
      "answerType",
      "remarks",
    ];
    const itemCols = [
      "itemId",
      "text",
      "exclusive",
      "condition",
      "random",
      "creative",
      // "answerType",
      "remarks",
    ];

    const loops = targetRows
      .filter((row) => row["rowType"].includes("表側"))
      .map((row) => {
        let output: StringObj = {};
        loopCols.forEach((col) => {
          output[col] = row[col];
        });
        return output;
      });

    const supplement = targetRows
      .filter(
        (row, idx) =>
          idx >= 1 &&
          row["questionType"] === "設問補足文" &&
          !pageBreaks.includes(targetRows[idx - 1]["questionType"])
      )
      .map((row) => {
        return {
          text: row["text"],
          condition: row["condition"],
          creative: row["creative"],
        };
      });

    let targetItemsRows: StringObj[] = [];
    const optionHeaders = [];
    let itemRows = [];

    if (loops.length) {
      itemRows = targetRows.filter(
        (row) =>
          (!row["rowType"] || row["rowType"].includes("表頭")) &&
          !row["rowType"].includes("表側") &&
          // row["itemId"] &&
          row["text"] &&
          !row["questionType"]
      );
      // itemRows = targetRows.filter((row) => row["rowType"] === "" && row["itemId"] !== "");
    } else {
      itemRows = targetRows.filter((row) => !row["rowType"] && row["itemId"] !== "");
    }

    const headerRowIndexes = itemRows.reduce((acc, row, index) => {
      if (row["itemId"].toString().includes("小見出し")) {
        return [...acc, index];
      } else {
        return acc;
      }
    }, []);

    targetItemsRows = itemRows
      .filter((row) => row["itemId"] !== "小見出し")
      .map((row) => {
        const itemRow: StringObj = {};
        itemCols.forEach((col) => {
          itemRow[col] = row[col];
        });
        return itemRow;
      });

    let curOptionLength = 0;

    itemRows.forEach((row, index) => {
      if (headerRowIndexes.includes(index)) {
        const headerLabel = row["text"];
        const headerRandom = row["random"];

        const targetHeaderIndex = headerRowIndexes.findIndex((i) => i === index);

        const isLastHeader =
          headerRowIndexes.findIndex((i) => i === index) === headerRowIndexes.length - 1;

        const thisRowsLength = isLastHeader
          ? itemRows.slice(index).length - 1
          : headerRowIndexes[targetHeaderIndex + 1] - headerRowIndexes[targetHeaderIndex] - 1;

        optionHeaders.push({
          label: headerLabel,
          from: curOptionLength + 1,
          to: curOptionLength + thisRowsLength,
          random: headerRandom,
        });
        curOptionLength += thisRowsLength;
      }
    });

    let supplementPrev = [];

    const forwardRows = targetRowsForward.filter((row, index) => {
      if (index != targetRowsForward.length - 1) {
        return (
          get(row, "questionType", "").includes("設問補足文") &&
          targetRowsForward[index + 1]["questionType"]
        );
      } else {
        return get(row, "questionType", "").includes("設問補足文");
      }
    });

    if (forwardRows.length) {
      supplementPrev = forwardRows.map((row) => {
        return {
          text: row["text"],
          condition: row["condition"],
          random: row["random"],
          creative: row["creative"],
          remarks: row["remarks"],
        };
      });
    }

    const isForwardPageBreak =
      targetRowsForward.length &&
      targetRowsForward.filter((r) => pageBreaks.includes(r["questionType"])).length
        ? true
        : false;

    // let isBackwardPageBreak = false;
    let questionIdRe = "";

    // if (!targetRowsBackward.map((row) => row["questionType"]).includes("ページの始まり")) {
    //   isBackwardPageBreak = false;
    // } else if (
    //   targetRowsBackward.length &&
    //   targetRowsBackward.map((row) => row["questionId"])[targetRowsBackward.length - 1] ==
    //     "ページの始まり"
    // ) {
    //   isBackwardPageBreak = true;
    // }

    if (judgeQuestionId(questionId)) {
      questionIdRe = questionId;
    } else {
      questionIdRe = `Q${questionId}`;
    }

    const titleRows = targetRows.filter(
      (row) =>
        (questionTypes.includes(row["questionType"]) && !row["rowType"]) ||
        row["rowType"] == "設問本文"
    );

    const firstRow = titleRows[0];

    const result = {
      questionId: questionIdRe,
      questionText: firstRow["text"],
      questionSupplementPrev: supplementPrev,
      questionSupplement: supplement,
      questionType: firstRow["questionType"],
      condition: firstRow["condition"],
      questionTitle: firstRow["questionTitle"],
      remarks: firstRow["remarks"],
      random: firstRow["random"],
      loop: loops,
      optionHeaders: optionHeaders,
      options: targetItemsRows,
      forwardPageBreak: isForwardPageBreak,
      // backwardPageBreak: isBackwardPageBreak,
      forward_text: "",
      backward_text: "",
    };
    return result;
  } catch (error) {
    console.error("Error getting question object:", error);
    return {
      questionId: questionId,
      questionText: "",
      questionSupplementPrev: [],
      questionSupplement: [],
      questionType: "",
      condition: "",
      questionTitle: "",
      remarks: "",
      random: "",
      loop: [],
      optionHeaders: [],
      options: [],
      forwardPageBreak: false,
      // backwardPageBreak: false,
      forward_text: "",
      backward_text: "",
    };
  }
}

export function getQuestionRowsCount(question: Question) {
  return (
    1 +
    (question.loop ? question.loop.length : 0) +
    question.options.length +
    (question.optionHeaders ? question.optionHeaders.length : 0) +
    (question.questionSupplement ? question.questionSupplement.length : 0) +
    (question.questionSupplementPrev ? question.questionSupplementPrev.length : 0)
  );
}
